import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { Breadcrumbs } from "../components/Breadcrumbs";
import { http } from '../auth/http';
import { ChevronRightIcon } from '../components/Icons';

type NodeType = 'grandeRegiao' | 'uf' | 'mesoRegiao' | 'microRegiao' | 'municipio';

interface BaseNode {
    id: number;
    name: string;
    sgl?: string;
    level: number;
    type: NodeType;
    isExpanded: boolean;
    isLoading: boolean;
    children: TreeNodeData[];
}

type GrandeRegiao = BaseNode & { type: 'grandeRegiao', data: { ideRegistro: number; desGrandeRegiao: string; sglGrandeRegiao: string; } };
type UF = BaseNode & { type: 'uf', data: { ideRegistro: number; desUF: string; sglUF: string; } };
type MesoRegiao = BaseNode & { type: 'mesoRegiao', data: { ideRegistro: number; desMesoRegiao: string; } };
type MicroRegiao = BaseNode & { type: 'microRegiao', data: { ideRegistro: number; desMicroRegiao: string; } };
type Municipio = BaseNode & { type: 'municipio', data: { ideRegistro: number; desMunicipio: string; } };

type TreeNodeData = GrandeRegiao | UF | MesoRegiao | MicroRegiao | Municipio;

const fetchChildren = async (node: TreeNodeData): Promise<TreeNodeData[]> => {
    switch(node.type) {
        case 'grandeRegiao': {
            const ufs = await http.get<any[]>(`/api/localizacao/ufs/${node.id}`);
            return ufs.map(uf => ({ id: uf.ideRegistro, name: uf.desUF, sgl: uf.sglUF, level: 1, type: 'uf', isExpanded: false, isLoading: false, children: [], data: uf }));
        }
        case 'uf': {
            const mesos = await http.get<any[]>(`/api/localizacao/meso-regioes/${node.id}`);
            return mesos.map(meso => ({ id: meso.ideRegistro, name: meso.desMesoRegiao, level: 2, type: 'mesoRegiao', isExpanded: false, isLoading: false, children: [], data: meso }));
        }
        case 'mesoRegiao': {
            const micros = await http.get<any[]>(`/api/localizacao/micro-regioes/${node.id}`);
            return micros.map(micro => ({ id: micro.ideRegistro, name: micro.desMicroRegiao, level: 3, type: 'microRegiao', isExpanded: false, isLoading: false, children: [], data: micro }));
        }
        case 'microRegiao': {
            const municipios = await http.get<any[]>(`/api/localizacao/municipios/${node.id}`);
            return municipios.map(mun => ({ id: mun.ideRegistro, name: mun.desMunicipio, level: 4, type: 'municipio', isExpanded: false, isLoading: false, children: [], data: mun }));
        }
        case 'municipio':
        default:
            return [];
    }
};

const TreeNode = ({ node, onToggle }: { node: TreeNodeData; onToggle: (node: TreeNodeData) => void; }) => {
    const hasChildren = node.type !== 'municipio';

    return (
        <li className="tree-node">
            <div className="tree-node-content" onClick={() => hasChildren && onToggle(node)} style={{ paddingLeft: `${node.level * 1.5}rem` }}>
                {hasChildren && (
                    <span className={`tree-node-toggle ${node.isExpanded ? 'expanded' : ''}`}>
                       <ChevronRightIcon />
                    </span>
                )}
                <span className="tree-node-label">
                    {node.name}
                    {node.sgl && <span className="sgl">{node.sgl}</span>}
                </span>
            </div>
            {node.isExpanded && (
                <ul className="tree-node-children">
                    {node.isLoading && <div className="loading">Carregando...</div>}
                    {!node.isLoading && node.children.length === 0 && <li><div style={{ paddingLeft: `${(node.level + 1) * 1.5}rem`, color: 'var(--text-secondary)' }}>Nenhum item encontrado.</div></li>}
                    {node.children.map(childNode => (
                        <TreeNode key={`${childNode.type}-${childNode.id}`} node={childNode} onToggle={onToggle} />
                    ))}
                </ul>
            )}
        </li>
    );
};

function Localizacao() {
    const [treeData, setTreeData] = useState<TreeNodeData[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const loadInitialData = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await http.get<any[]>('/api/localizacao/grandes-regioes');
            const initialNodes: GrandeRegiao[] = data.map(gr => ({
                id: gr.ideRegistro,
                name: gr.desGrandeRegiao,
                sgl: gr.sglGrandeRegiao,
                level: 0,
                type: 'grandeRegiao',
                isExpanded: false,
                isLoading: false,
                children: [],
                data: gr,
            }));
            setTreeData(initialNodes);
        } catch (error) {
            console.error("Failed to load initial location data:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadInitialData();
    }, [loadInitialData]);

    const handleToggleNode = useCallback(async (toggledNode: TreeNodeData) => {
        const updateNodeState = (nodes: TreeNodeData[], targetNode: TreeNodeData): TreeNodeData[] => {
            return nodes.map(node => {
                if (node.id === targetNode.id && node.type === targetNode.type) {
                    const isExpanding = !node.isExpanded;
                    if (isExpanding && node.children.length === 0) {
                        return { ...node, isExpanded: true, isLoading: true };
                    }
                    return { ...node, isExpanded: isExpanding };
                }
                if (node.children.length > 0) {
                    return { ...node, children: updateNodeState(node.children, targetNode) };
                }
                return node;
            });
        };
        
        setTreeData(prevTreeData => updateNodeState(prevTreeData, toggledNode));

        if (!toggledNode.isExpanded && toggledNode.children.length === 0) {
            const children = await fetchChildren(toggledNode);
            const updateWithChildren = (nodes: TreeNodeData[], parentNode: TreeNodeData): TreeNodeData[] => {
                return nodes.map(node => {
                    if (node.id === parentNode.id && node.type === parentNode.type) {
                        return { ...node, isLoading: false, children: children };
                    }
                    if (node.children.length > 0) {
                        return { ...node, children: updateWithChildren(node.children, parentNode) };
                    }
                    return node;
                });
            };
            setTreeData(prevTreeData => updateWithChildren(prevTreeData, toggledNode));
        }
    }, []);


    return (
        <div>
            <Breadcrumbs currentPage="Localização" />
            <header className="page-header">
                <h2 className="page-title">Estrutura Geográfica do Brasil</h2>
            </header>
            <div className="tree-view-container">
                {isLoading ? (
                    <div className="table-placeholder">Carregando dados de localização...</div>
                ) : (
                     <ul className="tree-view">
                        {treeData.map(node => (
                            <TreeNode key={`${node.type}-${node.id}`} node={node} onToggle={handleToggleNode} />
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export default Localizacao;
